import React, { useEffect, useState } from 'react';
import {
  TextField,
  Autocomplete,
  Box,
  Card,
  Grid,
  styled,
  Button,
  Switch,
  FormControlLabel,
  CircularProgress,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
  FormHelperText
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SaveIcon from '@mui/icons-material/Save';
import { Field, FieldProps, Formik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import {
  Category,
  PrinterProfile,
  ProductionCenter
} from '../../../../services/apiService/response-models';
import { getCategories, getPrinterProfileList } from 'src/services/apiService';

function GeneralSection(props: {
  model: ProductionCenter | null;
  isSaveInProgress: boolean;
  onSaveClick: () => void;
  onSave: (updatedModel: ProductionCenter) => Promise<boolean>;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isEdit = Boolean(props.model?.id);
  const [categories, setCategories] = useState<Category[]>([]);
  const [printerProfiles, setPrinterProfiles] = useState<PrinterProfile[]>([]);

  useEffect(() => {
    (async () => {
      const catsRes = await getCategories();
      setCategories(catsRes.data);

      const printerProfileRes = await getPrinterProfileList();
      setPrinterProfiles(printerProfileRes.data);
    })();
  }, []);

  const goBack = () => {
    navigate(-1);
  };

  return (
    <Card sx={{ p: 3 }}>
      <Formik
        initialValues={{
          name: props.model?.name || '',
          description: props.model?.description || '',
          printerProfileId: props.model?.printerProfileId,
          categoryIds: props.model?.categoryIds || [],
          itemIds: props.model?.itemIds || [],
          isActive: typeof (props.model as any)?.isActive === 'boolean' ? (props.model as any).isActive : true
        }}
        validationSchema={Yup.object().shape({
          name: Yup.string().max(250).required(t('The name field is required')),
          printerProfileId: Yup.string()
            .max(50)
            .required(t('The printer profile is required')),
          categoryIds: Yup.array()
            .of(Yup.string())
            .when('itemIds', (itemIds: string[], schema) => {
              if (!itemIds?.length)
                return schema.min(
                  1,
                  t('At least 1 category or item is required')
                );
            })
        })}
        onSubmit={async (_values, { resetForm, setStatus, setSubmitting }) => {
          try {
            var isSuccess = await props.onSave({
              ...props.model,
              ..._values
            });
            if (isSuccess) {
              resetForm();
              setStatus({ success: true });
              setSubmitting(false);
            }
          } catch (err) {
            console.error(err);
            setStatus({ success: false });
            setSubmitting(false);
          }
        }}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
          values,
          setFieldValue
        }) => (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="name"
                  placeholder={t('Enter name...')}
                  variant="outlined"
                  label={t('Production Center Name')}
                  error={Boolean(touched.name && errors.name)}
                  helperText={touched.name && errors.name}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.name}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="description"
                  placeholder={t('Enter description...')}
                  variant="outlined"
                  label={t('Description')}
                  error={Boolean(touched.description && errors.description)}
                  helperText={touched.description && errors.description}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  value={values.description}
                />
              </Grid>
              <Grid item xs={12}>
                <Field name="printerProfileId">
                  {({ field, form, meta }: FieldProps) => (
                    <FormControl
                      fullWidth
                      variant="outlined"
                      error={Boolean(errors.printerProfileId)}
                    >
                      <InputLabel htmlFor="country">
                        {t('Printer profile')}
                      </InputLabel>
                      <Select
                        label={t('country')}
                        inputProps={{
                          name: 'country'
                        }}
                        value={field.value}
                        onChange={(_event) => {
                          form.setFieldValue(field.name, _event.target.value);
                        }}
                      >
                        <MenuItem value="">
                          <em>Select...</em>
                        </MenuItem>
                        {printerProfiles.map(({ name, id }) => (
                          <MenuItem key={id} value={id}>
                            {t(name)}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.printerProfileId}</FormHelperText>
                    </FormControl>
                  )}
                </Field>
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  disablePortal
                  id="categoryIds"
                  options={categories}
                  getOptionLabel={(option) =>
                    typeof option === 'string' ? option : option.name
                  }
                  value={
                    Array.isArray(values.categoryIds)
                      ? categories.filter((p) =>
                          values.categoryIds.includes(p.id)
                        )
                      : []
                  }
                  onChange={(event, newValue) => {
                    const ids = newValue.map((p) =>
                      typeof p === 'string' ? p : p.id
                    );
                    setFieldValue('categoryIds', ids);
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option.id === (typeof value === 'string' ? value : value.id)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      variant="outlined"
                      label={t('Item Categories')}
                      placeholder={t('Select one or more categories')}
                      error={Boolean(touched.categoryIds && errors.categoryIds)}
                      helperText={touched.categoryIds && errors.categoryIds}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  labelPlacement="start"
                  control={
                    <Switch
                      name="isActive"
                      disabled={!isEdit}
                      color={isEdit ? 'primary' : 'secondary'}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      checked={values.isActive}
                    />
                  }
                  label={t('Is Active')}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  sx={{ mr: 2 }}
                  type="button"
                  onClick={goBack}
                  variant="text"
                >
                  {t('Cancel')}
                </Button>
                <Button
                  type="submit"
                  onClick={() => props.onSaveClick()}
                  variant="contained"
                  startIcon={
                    isSubmitting || props.isSaveInProgress ? (
                      <CircularProgress size="1rem" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  disabled={isSubmitting || props.isSaveInProgress}
                >
                  {t('Save')}
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
    </Card>
  );
}

export default GeneralSection;
